import { useEffect, useState } from 'react'
import { User } from '../../types'
import { PERMISSION_GROUPS, isSuperAdmin } from '../../lib/permissions'
import { usersService, AdminUser } from '../../services/usersService'
import { auditService } from '../../services/auditService'
import { MOBILE_RE } from '../../services/buyerService'
import { toast } from '../Toast'

/**
 * Winnie, 15 September — Users & Permissions:
 *
 *   email   string, required   // currently may be optional — confirm and enforce
 *   mobile  string, required   // currently may be optional — confirm and enforce
 *   Both fields block Create/Save until populated
 *
 * Confirmed: mobile was not on this form at all. `user_profiles.phone` has existed since
 * migration 001 and nothing in the admin panel ever wrote to it, which is why the SMS features
 * built in Week 4 have no number to send an internal notification to. Email was collected but
 * only checked for emptiness, and only on the Create path.
 *
 * Both are now required, both block the button rather than failing on submit, and the mobile is
 * validated as an Australian mobile with the same regex the buyer records use — a landline in
 * this field is a number no SMS will ever reach, which is the only reason the field exists.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface UserEditPanelProps {
  actor: User
  editing: AdminUser | null // null = add mode
  onClose: () => void
  onSaved: (message: string, isError?: boolean) => void
}

export default function UserEditPanel({ actor, editing, onClose, onSaved }: UserEditPanelProps) {
  const isAdd = editing === null
  const [fullName, setFullName] = useState(editing?.full_name || '')
  const [email, setEmail] = useState(editing?.email || '')
  const [mobile, setMobile] = useState(editing?.phone || '')
  const [isActive, setIsActive] = useState(editing?.is_active ?? true)
  const [superAdmin, setSuperAdmin] = useState(editing?.is_super_admin || false)
  const [perms, setPerms] = useState<Record<string, boolean>>({ ...(editing?.permissions || {}) })
  const [saving, setSaving] = useState(false)
  /* Shown in the panel rather than only as a toast that has already gone by the time you
     look back at the form. A refusal from the server is about this form, so it belongs on it. */
  const [failure, setFailure] = useState<string | null>(null)

  /* Only a super admin can make another one — the server enforces it, and a control that
     cannot work should not be offered. See create-user.js for why. */
  const actorIsSuper = isSuperAdmin(actor)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const togglePerm = (key: string) => setPerms((p) => ({ ...p, [key]: !p[key] }))

  /* The three tests the Save button is gated on, each with the sentence it would refuse with.
     Kept as data rather than an if-chain so the button state and the message can never
     disagree about why it is disabled. */
  const nameOk = !!fullName.trim()
  const emailOk = EMAIL_RE.test(email.trim())
  const mobileOk = MOBILE_RE.test(mobile.replace(/\s/g, ''))
  const blocker = !nameOk
    ? 'A full name is required.'
    : !email.trim()
      ? 'An email address is required.'
      : !emailOk
        ? 'That does not look like an email address.'
        : !mobile.trim()
          ? 'A mobile number is required.'
          : !mobileOk
            ? 'That is not an Australian mobile number (04xx xxx xxx).'
            : null

  const save = async () => {
    if (blocker) {
      setFailure(blocker)
      onSaved(blocker, true)
      return
    }
    setSaving(true)
    setFailure(null)
    try {
      const effectivePerms = superAdmin ? {} : perms
      if (isAdd) {
        const res = await usersService.createUser({
          full_name: fullName.trim(),
          email: email.trim(),
          phone: mobile.replace(/\s/g, ''),
          permissions: effectivePerms,
          is_super_admin: superAdmin,
        })
        if (!res.success) {
          /* The panel stays open with everything still typed in. Whatever went wrong —
             a duplicate address, a permission, a mobile the server rejected — is fixable
             here, and closing the form would make the administrator type it all again. */
          setFailure(res.message)
          onSaved(res.message, true)
          setSaving(false)
          return
        }
        await auditService.log(actor, 'user_created', {
          targetName: fullName.trim(),
          details: { email: email.trim(), is_super_admin: superAdmin },
        })
        /* One confirmation sentence, platform-wide (Winnie, 15 Sep). The set-password email
           that createUser also sends is a separate fact, so it keeps its own words. */
        toast.saved()
        if (res.message && !/^\S+ created\.$/.test(res.message)) onSaved(res.message)
        else onSaved('')
      } else {
        await usersService.updateUser(editing!.id, {
          full_name: fullName.trim(),
          phone: mobile.replace(/\s/g, ''),
          is_active: isActive,
          is_super_admin: superAdmin,
          permissions: effectivePerms,
        })
        await auditService.log(actor, 'user_updated', {
          targetUserId: editing!.id,
          targetName: fullName.trim(),
          details: { is_super_admin: superAdmin, is_active: isActive, permissions: effectivePerms },
        })
        toast.saved()
        onSaved('')
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Save failed'
      setFailure(message)
      onSaved(message, true)
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async () => {
    if (!editing) return
    setSaving(true)
    try {
      await usersService.deactivateUser(editing.id)
      await auditService.log(actor, 'user_deactivated', { targetUserId: editing.id, targetName: editing.full_name })
      onSaved(`User deactivated: ${editing.full_name}`)
    } catch (e) {
      onSaved(e instanceof Error ? e.message : 'Deactivate failed', true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <div className="admin-overlay" onClick={onClose} />
      <div className="admin-panel">
        <div className="admin-panel-header">
          <div className="admin-panel-title">{isAdd ? 'Add User' : 'Edit User'}</div>
          <button className="admin-panel-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="admin-panel-body">
          <div className="admin-field">
            <label>Full Name</label>
            <input className="admin-input" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g., Tuo T." />
          </div>
          <div className="admin-field">
            <label>Email</label>
            <input
              className="admin-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@directauto.net.au"
              disabled={!isAdd}
              type="email"
              required
            />
            {!!email.trim() && !emailOk && (
              <small className="admin-hint" style={{ color: 'var(--red)' }}>
                That does not look like an email address.
              </small>
            )}
          </div>
          <div className="admin-field">
            <label>Mobile</label>
            <input
              className="admin-input"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="0412 345 678"
              type="tel"
              required
            />
            {/* The old message said only that the number was wrong. The commonest reason is a
                digit short, which is invisible unless you count them — so say the shape. */}
            <small className="admin-hint" style={!!mobile.trim() && !mobileOk ? { color: 'var(--red)' } : undefined}>
              {!!mobile.trim() && !mobileOk
                ? `That is not an Australian mobile number — 10 digits starting 04, or +61 4… (you have ${mobile.replace(/\D/g, '').length} digits).`
                : 'Australian mobile, e.g. 0412 345 678 or +61 412 345 678. Used for the platform’s SMS alerts.'}
            </small>
          </div>

          {!isAdd && (
            <div className="admin-field">
              <label>Status</label>
              <select className="admin-input" value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}

          <label
            className="admin-check-row"
            style={{ marginTop: 6, marginBottom: actorIsSuper ? 18 : 6, opacity: actorIsSuper ? 1 : 0.55 }}
            title={actorIsSuper ? undefined : 'Only a super admin can grant super admin.'}
          >
            <input
              type="checkbox"
              checked={superAdmin}
              disabled={!actorIsSuper}
              onChange={(e) => setSuperAdmin(e.target.checked)}
            />
            <span>
              <strong>Super Admin</strong> — all permissions
            </span>
          </label>
          {!actorIsSuper && (
            <div className="admin-hint" style={{ marginBottom: 18 }}>
              Only a super admin can grant super admin. Everything else on this form is yours to set.
            </div>
          )}

          {superAdmin ? (
            <div className="super-notice">✓ Super Admin has every permission enabled. Individual permissions are managed automatically.</div>
          ) : (
            PERMISSION_GROUPS.map((group) => (
              <div className="perm-section" key={group.title}>
                <div className="perm-section-title">{group.title}</div>
                {group.permissions.map((p) => (
                  <label className="perm-item" key={p.key}>
                    <input type="checkbox" checked={!!perms[p.key]} onChange={() => togglePerm(p.key)} />
                    <span className="permission-info">
                      <span className="perm-name">{p.name}</span>
                      <div className="perm-desc">{p.description}</div>
                    </span>
                  </label>
                ))}
              </div>
            ))
          )}
        </div>

        <div className="admin-panel-footer">
          {/* Three things the footer has to say, in the order they matter: what went wrong,
              why Save is refusing, and — when nothing is wrong — what Save is about to do.
              The last one was missing entirely: adding a user sends them an email, and an
              administrator should know that before clicking, not after. */}
          {(failure || blocker || isAdd) && (
            <div
              className="admin-hint"
              role={failure || blocker ? 'alert' : undefined}
              style={{ flex: '1 1 100%', marginBottom: 8, color: failure || blocker ? 'var(--red)' : undefined }}
            >
              {failure || blocker || `Saving creates the account and emails ${email.trim() || 'them'} an invitation to set a password.`}
            </div>
          )}
          <button className="abtn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          {!isAdd && (
            <button className="abtn danger" onClick={deactivate} disabled={saving || !editing?.is_active}>
              Deactivate
            </button>
          )}
          {/* Winnie, 15 Sep: "Both fields block Create/Save until populated." Blocked rather
              than refused after the fact, with the reason on the button's title so it is not a
              dead control with no explanation. */}
          <button className="abtn primary" onClick={save} disabled={saving || !!blocker} title={blocker || undefined}>
            {saving ? (isAdd ? 'Creating…' : 'Saving…') : isAdd ? 'Create & invite' : 'Save'}
          </button>
        </div>
      </div>
    </>
  )
}
